import { useContext, useEffect, useMemo, useState } from 'react';
import SessionContext from '../../../library/Core/SessionContext';
import { GlobalContext } from '../../../library/Core/GlobalContext';
import ProfileAnalyticsDashboard from '../../admin/profile-analytics-dashboard/ProfileAnalyticsDashboard';
import ProfileAnalyticsService from '../../admin/profile-analytics-dashboard/profileAnalyticsService';
import { mergeProfileAnalyticsData } from '../../admin/profile-analytics-dashboard/mergeUtils';
import { ProfileAnalyticsData } from '../../admin/profile-analytics-dashboard/types';

const ALL_VALUE = '__ALL__';

// Institute-Admin entry point (Dashboard > Profile Analytics Dashboard).
// For a group/multi-campus admin, a selector in the header picks which institute's
// dashboard to view; a single-institute admin sees no selector.
const ProfileAnalyticsDashboardPage = () => {
    const { sessionState } = useContext(SessionContext);
    const { globalState } = useContext(GlobalContext);

    const primaryInstitute = sessionState?.primaryInstitute;

    // Resolve the admin's institutes (codes) to {label, value} using domain data.
    const rawInstituteOptions = useMemo(() => {
        const codes: any[] = Array.isArray(sessionState?.institutes) ? sessionState!.institutes : [];
        const domain = globalState?.domain?.get('INSTITUTE_DOMAIN') ?? [];
        const opts = codes
            .map((code: any) => {
                const campus = domain.find((item: any) => item.code == code);
                return campus ? { label: campus.value, value: String(campus.code) } : null;
            })
            .filter(Boolean) as { label: string; value: string }[];
        // De-dupe and sort by name for a stable dropdown.
        const seen = new Set<string>();
        return opts
            .filter((o) => (seen.has(o.value) ? false : (seen.add(o.value), true)))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [sessionState, globalState]);

    const isGroup = rawInstituteOptions.length > 1;

    // "All Institutes" is only offered when the admin manages more than one institute,
    // and is the default selection so the merged view loads first.
    const instituteOptions = useMemo(
        () => (isGroup ? [{ label: 'All Institutes', value: ALL_VALUE }, ...rawInstituteOptions] : rawInstituteOptions),
        [isGroup, rawInstituteOptions]
    );

    const [selectedInstituteId, setSelectedInstituteId] = useState<string>(
        isGroup ? ALL_VALUE : primaryInstitute ? String(primaryInstitute) : rawInstituteOptions[0]?.value || ''
    );

    const isAllSelected = isGroup && selectedInstituteId === ALL_VALUE;
    const activeInstituteId = isGroup ? selectedInstituteId : primaryInstitute;

    // Fetch + merge every institute's dashboard when "All Institutes" is selected.
    const [mergedData, setMergedData] = useState<ProfileAnalyticsData | null>(null);
    const [mergedLoading, setMergedLoading] = useState(false);

    useEffect(() => {
        if (!isAllSelected) return;
        let active = true;
        setMergedLoading(true);
        Promise.all(
            rawInstituteOptions.map((o) =>
                ProfileAnalyticsService.getSnapshotDashboard(o.value)
                    .then((res) => res?.data ?? null)
                    .catch(() => null)
            )
        )
            .then((results) => {
                if (!active) return;
                const valid = results.filter((d): d is ProfileAnalyticsData => !!d);
                setMergedData(mergeProfileAnalyticsData(valid));
            })
            .finally(() => active && setMergedLoading(false));
        return () => {
            active = false;
        };
    }, [isAllSelected, rawInstituteOptions]);

    if (!activeInstituteId) {
        return (
            <div className="main flex-1">
                <div className="container max-1140 px-lg-0 text-center p-5 text-muted">
                    No institute is associated with your account.
                </div>
            </div>
        );
    }

    if (isAllSelected && mergedLoading) {
        return (
            <div className="main flex-1">
                <div className="container max-1140 px-lg-0 text-center p-5 text-muted">
                    <i className="icon-spinner spin font-22 me-2" aria-hidden="true"></i>
                    Loading Profile Analytics…
                </div>
            </div>
        );
    }

    return (
        <ProfileAnalyticsDashboard
            instituteId={activeInstituteId}
            data={isAllSelected ? mergedData : undefined}
            instituteOptions={isGroup ? instituteOptions : undefined}
            selectedInstituteId={selectedInstituteId}
            onInstituteChange={setSelectedInstituteId}
        />
    );
};

export default ProfileAnalyticsDashboardPage;
